import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useLocation, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { useAuth } from 'firebaseHooks/AuthContext';
import useGlobal from 'global-state/store';
import { doc, getDoc } from 'firebase/firestore';
import OrganizationAdminView from 'components/organizations/admin/Organization';
import OrganizationMemberView from 'components/organizations/member/Organization';

export default function Organization(props) {
  const location = useLocation();
  const [organization] = useState(
    location?.state?.organization,
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!organization) {
      navigate('/');
    }
  }, [navigate, organization]);

  if (!organization) {
    return null;
  }
  return (<OrganizationContent organization={organization} />);
}

function OrganizationContent({ organization }) {
  const [loadingState, setloadingState] = useState('initial');
  const [member, setmember] = useState(undefined);
  const db = useFirestore();
  const { currentUser } = useAuth();
  const [globalState] = useGlobal();
  useEffect(() => {
    async function loadMember() {
      const docRef = doc(db, 'organizations', organization.id, 'members', currentUser.uid);
      const colSnap = await getDoc(docRef);

      // If user is a super admin but not a member of this organization, create a minimal member object
      if (!colSnap.exists() && globalState.wsRoles.includes('admin')) {
        const udocRef = doc(db, 'users', currentUser.uid);
        const udocSnap = await getDoc(udocRef);
        setmember({
          id: currentUser.uid,
          email: udocSnap.data()?.email || 'Unknown',
          role: 'super_admin',
          membershipState: 'super_admin',
        });
        setloadingState('loaded');
        return;
      }

      if (colSnap.exists()) {
        loadUser({ ...colSnap.data(), id: currentUser.uid });
      } else {
        setloadingState('loaded');
      }
    }

    async function loadUser(firstVersionMember) {
      const udocRef = doc(db, 'users', currentUser.uid);
      const udocSnap = await getDoc(udocRef);
      loadMembership({ ...firstVersionMember, email: udocSnap.data()?.email || 'Unknown' });
    }

    async function loadMembership(secondVersionMember) {
      const udocRef = doc(db, 'organizations', organization.id, 'members', currentUser.uid, 'data', 'membership');
      const udocSnap = await getDoc(udocRef);
      setmember({
        ...secondVersionMember,
        membershipState: udocSnap.exists() && udocSnap.data()
          ? udocSnap.data().membershipState
          : 'unknown',
      });
      setloadingState('loaded');
    }
    if (loadingState === 'initial' && currentUser?.uid !== undefined) {
      setloadingState('loading');
      loadMember();
    }
  }, [db, loadingState, organization.id, currentUser?.uid, globalState.wsRoles]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        width: '100%',
      }}
    >
      <Typography component="span" sx={{ fontWeight: 'bold' }} variant="h3">{organization.name}</Typography>
      {loadingState !== 'loaded' && (
        <CircularProgress />
      )}
      {loadingState === 'loaded' && (member?.role === 'admin' || globalState.wsRoles.includes('admin')) && (
        <OrganizationAdminView organization={organization} />
      )}
      {loadingState === 'loaded' && member?.role !== 'admin' && !globalState.wsRoles.includes('admin') && (
        <OrganizationMemberView organization={organization} member={member} />
      )}
    </Box>
  );
}
